import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { apiPostFormData, apiputFormData } from "../../API/Api_Url";
import { toast } from "react-toastify";

function CreateMaterialGRNModal({ isOpen, onClose, request, GetUserAllDeliveredMaterialRequest }) {
    const [IsLoading, setIsLoading] = useState(false);
    const [status, setStatus] = useState("Accepted");
    const [verifiedQuantities, setVerifiedQuantities] = useState([]);
    const [qcChecklist, setQcChecklist] = useState([]);
    const [reason, setReason] = useState("");
    const [photos, setPhotos] = useState([]);

    useEffect(() => {
        if (request?.MaterialItems?.length > 0) {
            setVerifiedQuantities(
                request.MaterialItems.map(item => ({
                    materialName: item.materialName,
                    receivedQuantity: item.quantity,
                }))
            );
            setQcChecklist(
                request.MaterialItems.map(item => ({
                    item: item.materialName,
                    status: true,
                }))
            );
        }
    }, [request]);

    if (!isOpen) return null;

    const handleQuantityChange = (index, value) => {
        const updated = [...verifiedQuantities];
        updated[index].receivedQuantity = value;
        setVerifiedQuantities(updated);
    };

    const handleQCChange = (index, value) => {
        const updated = [...qcChecklist];
        updated[index].status = value;
        setQcChecklist(updated);
    };



    const UserAddPaymentTransaction = async () => {
        if (!status) {
            toast.error("Please select GRN status (Accepted / Rejected)");
            return;
        }
        if (status === "Rejected" && !reason.trim()) {
            toast.error("Please provide a reason for rejection");
            return;
        }

        if (!verifiedQuantities || verifiedQuantities.length === 0) {
            toast.error("Please verify material quantities");
            return;
        }

        if (!qcChecklist || qcChecklist.length === 0) {
            toast.error("Please complete the QC checklist");
            return;
        }
        if (photos.length === 0) {
            toast.error("Please upload at least one photo");
            return;
        }

        const formData = new FormData();
        photos.forEach(file => formData.append("Photos", file));

        formData.append("VerifiedQuantity", JSON.stringify(verifiedQuantities));
        formData.append("QCChecklist", JSON.stringify(qcChecklist));
        formData.append("Status", status);
        if (status === "Rejected") formData.append("Reason", reason);

        try {
            setIsLoading(true);
            const response = await apiPostFormData(`api/user/UserCreateAndApproveGRN/${request?._id}`, formData);

            if (response?.data) {
                toast.success(response?.data?.message || response?.message);
                GetUserAllDeliveredMaterialRequest();
                onClose()
            } else {
                toast.error(response?.message || "Failed to add payment");
            }
        } catch (error) {
            console.error("Error adding payment:", error);
            toast.error("Something went wrong. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };



    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
            <div className="bg-white rounded-2xl shadow-lg w-full max-w-2xl p-6 relative">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                    <X className="w-5 h-5" />
                </button>

                <h2 className="text-xl font-bold text-gray-800 mb-4">
                    Create GRN – Material Request #{request?._id?.slice(-6)}
                </h2>
                <div className="mb-4">
                    <label className="text-sm font-medium text-gray-700">Status</label>
                    <select
                        className="form-select w-full mt-1 border rounded-lg px-3 py-2"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                    >
                        <option value="Accepted">Accepted</option>
                        <option value="Rejected">Rejected</option>
                    </select>
                </div>
                {status === "Rejected" && (
                    <div className="mb-4">
                        <label className="text-sm font-medium text-gray-700">Reason</label>
                        <textarea
                            className="form-textarea w-full mt-1 border rounded-lg px-3 py-2"
                            rows={3}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Enter reason for rejection..."
                        />
                    </div>
                )}
                {verifiedQuantities.length > 0 && (
                    <div className="mb-4">
                        <h4 className="font-semibold text-gray-800 mb-2">Verified Quantities</h4>
                        <div className="space-y-2">
                            {verifiedQuantities.map((item, index) => (
                                <div
                                    key={index}
                                    className="flex items-center justify-between border p-2 rounded-lg"
                                >
                                    <span className="text-sm font-medium">{item.materialName}</span>
                                    <input
                                        type="number"
                                        value={item.receivedQuantity}
                                        onChange={(e) =>
                                            handleQuantityChange(index, e.target.value)
                                        }
                                        className="w-24 border rounded px-2 py-1 text-sm"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {qcChecklist.length > 0 && (
                    <div className="mb-4">
                        <h4 className="font-semibold text-gray-800 mb-2">QC Checklist</h4>
                        <div className="space-y-2">
                            {qcChecklist.map((item, index) => (
                                <div
                                    key={index}
                                    className="flex items-center justify-between border p-2 rounded-lg"
                                >
                                    <span className="text-sm font-medium">{item.item}</span>
                                    <select
                                        className="border rounded px-2 py-1 text-sm"
                                        value={item.status}
                                        onChange={(e) =>
                                            handleQCChange(index, e.target.value === "true")
                                        }
                                    >
                                        <option value={true}>Pass</option>
                                        <option value={false}>Fail</option>
                                    </select>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="mb-4">
                    <label className="text-sm font-medium text-gray-700">Upload Photos</label>
                    <input
                        type="file"
                        multiple
                        className="block w-full mt-2 text-sm"
                        onChange={(e) => setPhotos([...e.target.files])}
                    />
                </div>

                <div className="flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => { UserAddPaymentTransaction() }}
                        className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                    >
                        {IsLoading ? "Please Wait" : "Submit GRN "}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CreateMaterialGRNModal;
